import * as yup from 'yup';

import {
  ArrivedNCOfferingStatus,
  AssetType,
  OfferingStatus,
  OfferingTradeProcessingStatus,
  OfferingType,
  SecurityType,
} from '@arrived/models';

export type OfferingPostRequest = yup.InferType<typeof offeringPostSchema>;
export type OfferingPatchRequest = yup.InferType<typeof offeringPatchSchema>;

const REQUIRED_STRING = 'This is required';

export const offeringPostSchema = yup
  .object({
    type: yup.mixed<OfferingType>().oneOf(Object.values(OfferingType)).required(REQUIRED_STRING),
    name: yup.string().trim().required(REQUIRED_STRING),
    status: yup.mixed<OfferingStatus>().oneOf(Object.values(OfferingStatus)).required(REQUIRED_STRING),
    tradeProcessingStatus: yup
      .mixed<OfferingTradeProcessingStatus>()
      .oneOf(Object.values(OfferingTradeProcessingStatus))
      .required(REQUIRED_STRING),
    ncStatus: yup
      .mixed<ArrivedNCOfferingStatus>()
      .oneOf(Object.values(ArrivedNCOfferingStatus))
      .required(REQUIRED_STRING),
    issuerId: yup.number().positive().required(REQUIRED_STRING),
    assetType: yup.mixed<AssetType>().oneOf(Object.values(AssetType)).required(REQUIRED_STRING),
    shortName: yup.string().trim().required(REQUIRED_STRING),
    llcName: yup.string().trim().required(REQUIRED_STRING),
    description: yup.string().trim().required(REQUIRED_STRING),
    targetRaiseAmount: yup.number().positive().required(REQUIRED_STRING),
    qualifiedRaiseAmount: yup
      .number()
      .positive()
      .when('targetRaiseAmount', (targetRaiseAmount, schema) => {
        return schema.test({
          test: (qualifiedRaiseAmount: number) => !!targetRaiseAmount && qualifiedRaiseAmount >= targetRaiseAmount,
          message: 'Qualified should be greater then or equal target amount',
        });
      })
      .required(REQUIRED_STRING),
    sharePrice: yup.number().positive().required(REQUIRED_STRING),
    // TODO: Can we do validation on this here? Make sure that it's equal to targetRaiseAmount / sharePrice
    totalShares: yup.number().positive().required(REQUIRED_STRING),
    startAt: yup.date().required(REQUIRED_STRING),
    endAt: yup.date().required(REQUIRED_STRING),
    holdPeriodYearsMin: yup.number().integer().positive().required(REQUIRED_STRING),
    holdPeriodYearsMax: yup
      .number()
      .integer()
      .positive()
      .required(REQUIRED_STRING)
      .when('holdPeriodYearsMin', (holdPeriodYearsMin, schema) =>
        holdPeriodYearsMin != null ? schema.min(holdPeriodYearsMin) : schema
      ),
    minTransactionAmount: yup.number().positive().required(REQUIRED_STRING),
    debtPercent: yup.number().required(REQUIRED_STRING),
    debtInterestPercent: yup.number().required(REQUIRED_STRING),
    debtAmount: yup.number().required(REQUIRED_STRING),
    arrivedOneTimeProceeds: yup.number().min(0).required(REQUIRED_STRING),
    isFund: yup.boolean().required(REQUIRED_STRING),
    securityType: yup.mixed<SecurityType>().oneOf(Object.values(SecurityType)).required(REQUIRED_STRING),
    isEarlyAccess: yup.boolean().required(REQUIRED_STRING),
    marketId: yup.number().required(REQUIRED_STRING),
    closingOfferingAndHoldingCosts: yup.number().required(REQUIRED_STRING),
    propertyImprovementsAndCashReserves: yup.number().required(REQUIRED_STRING),
    quarterlyAumFeeAmount: yup.number().nullable(),
    projectedFirstDividendDate: yup.string().trim().nullable(),
    projectedFirstDividendAmount: yup.string().trim().nullable(),
    projectedAnnualDividendYield: yup
      .number()
      .transform((value) => (isNaN(value) ? null : value))
      .nullable(),
    projectedAppreciationLower: yup
      .number()
      .nullable()
      .lessThan(1, 'Lower appreciation must be less than 1')
      .moreThan(-0.5, 'Appreciation must be more than -0.5'),
    projectedAppreciationUpper: yup
      .number()
      .nullable()
      .lessThan(1, 'Upper appreciation must be less than 1')
      .moreThan(-0.5, 'Appreciation must be more than -0.5')
      .moreThan(yup.ref('projectedAppreciationLower'))
      .when('projectedAppreciationLower', {
        is: (projectedAppreciationLower: unknown) => !!projectedAppreciationLower || projectedAppreciationLower === 0,
        then: (schema) => schema.required(),
        otherwise: (schema) => schema.optional(),
      }),
  })
  .required();

export const offeringPatchSchema = yup
  .object({
    id: yup.number().integer().positive().required(),
  })
  .concat(offeringPostSchema)
  .defined();

export const propertyIdSchema = yup.object({ propertyId: yup.number().integer().positive().required() }).defined();
